import { Component, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
    AnnualReportProvider,
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully,
} from '@modeso/aumico-lib-annualreport-fe';
import { AumicoCoreLayoutProvider, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import { NavigationItem } from '../../../../../../../aumico-components/src/lib';
import {
    AnnualReportStep,
    IAnnualReport,
    IFinStatements,
    IFxAccountHistorical,
    IFxAccountMethods,
    IFxHistoricalArchiveYear,
    IFxHistoricalOpening,
    IFxMovement,
    IFxPeriodRates,
    IFxRoundingAccounts,
    IFxTranslation,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import Debug from 'debug';
import { Observable, combineLatest, debounceTime, of } from 'rxjs';
import { map } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { Paths } from '../../../../enums/paths.enum';
import { RouteLabels } from '../../../../enums/routeLabels.enum';
import { AnnualReportSubPage } from '../../annual-report-sub-page';
import {
    IFxHistoricalEditRequest,
    IFxMethodChange,
    IFxRoundingAccountChange,
} from './fx-conversion-table/fx-conversion-table.component';
import { IFxHistoricalSavePayload } from './fx-historical-modal/fx-historical-modal.component';
import { FxTranslationHelper } from '../../utils/FxTranslationHelper';

const debug = Debug('aumico:saas:FxRatesComponent');

/** XLSX.read is synchronous, so an oversized file would freeze the tab rather than fail. */
const MAX_RATE_FILE_SIZE_MB = 5;

/** The only two columns the import reads, found by their header (the Kendris export's names). */
const RATE_FILE_DATE_HEADER = 'date';
const RATE_FILE_RATE_HEADER = 'actual rate';
/** How far down a sheet the header row may sit. */
const RATE_FILE_HEADER_SEARCH_ROWS = 20;

interface IParsedRateSeries {
    average: number;
    /** The rate on the balance sheet date — or on the last business day before it, when it is a weekend. */
    closing: number | null;
    /** Set only when the closing rate comes from a business day before the balance sheet date. */
    closingDate: Date | null;
}

type RateImportTarget = 'currentYear' | 'previousYear';

/** An import problem as shown below the drop zone — it names the cause, and stays until the next file. */
interface IRateImportError {
    messageKey: string;
    params?: Record<string, string>;
}

/** A rate file the import can name the problem of, instead of the generic read error. */
class RateFileError extends Error implements IRateImportError {
    constructor(readonly messageKey: string, readonly params?: Record<string, string>) {
        super(messageKey);
    }
}

@UntilDestroy()
@Component({
    selector: 'aumico-fx-rates',
    templateUrl: './fx-rates.component.html',
    styleUrls: ['./fx-rates.component.scss'],
})
export class FxRatesComponent extends AnnualReportSubPage implements OnInit {
    readonly rateDecimalPlaces = FxTranslationHelper.RATE_DECIMAL_PLACES;
    readonly estvUrl = 'https://www.ictax.admin.ch';

    readonly stepPeriodRates = 0;
    readonly stepConversionTable = 1;

    navigationItems: NavigationItem[] = [
        { name: 'aumico_annual_report_fx_rates_step_period', active: true },
        { name: 'aumico_annual_report_fx_table_step', active: false },
    ];
    selectedTab = this.stepPeriodRates;

    readonly groupingMaskingPath = Paths.GROUPING_MASKING;
    readonly groupingMaskingLabel = RouteLabels.FINANCIAL_STATEMENT_GROUPING_MASKING;

    form: FormGroup;

    functionalCurrencyName = '';
    displayCurrencyName = '';
    currencyPairLabel = '';

    currentYearLabel = '';
    previousYearLabel = '';
    currentYearClosingDate = '';
    previousYearClosingDate = '';

    previousYearLocked = false;

    showUnlockConfirmation = false;
    showImportConfirmation = false;
    pendingSeries: IParsedRateSeries | null = null;
    showRateFileGuide = false;
    importErrors: Partial<Record<RateImportTarget, IRateImportError>> = {};
    private importTargetYear: RateImportTarget = 'currentYear';

    /** Set only by the footer's "save and continue"; the plain Save button must not switch step. */
    private advanceToTableOnSave = false;
    /** Evaluated once per page visit: a report whose rates are already saved opens on the table. */
    private hasAppliedInitialStep = false;
    /**
     * Set once the user works the lock switch themselves. From then on their choice wins for the
     * session in BOTH directions — a store re-emission must never flip the lock back under them.
     */
    private hasUserOverriddenLock = false;
    private carriedForwardRates: IFxPeriodRates | null = null;
    private carriedForwardMethods: Record<string, Record<string, IFxAccountMethods>> | null = null;
    /** The prior close's historical data — its movements become this report's archived prior year. */
    private carriedForwardHistorical: Record<string, Record<string, IFxAccountHistorical>> | null = null;
    private carriedForwardBalanceDate: string | null = null;
    /** The prior close's own choice for its year — it becomes this report's prior-year choice. */
    private carriedForwardRounding: IFxRoundingAccounts['currentYear'] | null = null;
    private hasAppliedCarriedForwardRates = false;
    currentAnnualReport: IAnnualReport | undefined;
    private balanceSheetDate: Date | undefined;

    finStatements: IFinStatements[] | undefined;
    /** The statements have emitted at least once — the carry-forward waits for them to re-key. */
    private hasFinStatements = false;

    /** The working copy handed to the conversion table; rebuilt on every relevant change. */
    conversionFxTranslation: IFxTranslation | undefined;
    private accountMethods: Record<string, Record<string, IFxAccountMethods>> = {};
    private methodsDirty = false;

    /** Per-account historical rates / movements, same working-copy pattern as accountMethods. */
    private accountHistorical: Record<string, Record<string, IFxAccountHistorical>> = {};
    /** Where each year's year-result rounding difference is booked, same working-copy pattern. */
    private roundingAccounts: IFxRoundingAccounts = {};
    /** The account-year the modal is editing; null while it is closed. */
    historicalRequest: IFxHistoricalEditRequest | null = null;

    constructor(
        injector: Injector,
        public override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        protected override actionsListener$: ActionsSubject,
        private readonly annualReportProvider: AnnualReportProvider,
        private readonly formBuilder: FormBuilder,
        dialog: MatDialog,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);
        this.currentAnnualReportStep = AnnualReportStep.IMPORT_BALANCE_INCOME;
        this.form = this.formBuilder.group({
            currentYearClosing: [null, Validators.required],
            currentYearAverage: [null, Validators.required],
            previousYearClosing: [null, Validators.required],
            previousYearAverage: [null, Validators.required],
        });
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.annualReportProvider
            .getActiveAnnualReport$()
            .pipe(untilDestroyed(this))
            .subscribe((annualReport: IAnnualReport) => {
                if (annualReport == null) {
                    return;
                }
                this.currentAnnualReport = annualReport;
                this.applyAnnualReport(annualReport);
            });

        this.form.statusChanges.pipe(untilDestroyed(this)).subscribe(() => this.syncFooterButtons());
        // Every emission re-decorates both full statements in the conversion table — debounce
        // typing instead of recomputing per keystroke.
        this.form.valueChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), untilDestroyed(this))
            .subscribe(() => this.updateConversionInput());
        this.syncFooterButtons();

        this.annualReportProvider
            .getActiveFinStatementsWithReclasses$()
            .pipe(untilDestroyed(this))
            .subscribe((finStatements) => {
                this.finStatements = finStatements;
                this.hasFinStatements = true;
                this.applyCarriedForwardRates();
            });

        // The comparative is only a source when the user actually asked for it in the general
        // information — the same question the upload/mapping step asks
        // (`isPreviousDataTakenFromPreviousAnnualReport`). Deselecting it leaves both the saved
        // `autoFillAnnualReportId` and the loaded report in the store, so the presence of that
        // report says nothing about the user's choice; only the flag does. The balance flag
        // governs: these rates translate balance-sheet positions and come from the BALANCE
        // comparative. Combined rather than read ad hoc, so the flag is never missed when the
        // comparative resolves before the active report.
        combineLatest([
            this.annualReportProvider.getActiveAnnualReport$(),
            this.annualReportProvider.getAnnualReportForBalanceAutoFill$(),
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([annualReport, autoFillAnnualReport]) => {
                const comparative =
                    annualReport?.generalInformation?.isPreviousDataTakenFromPreviousAnnualReport === true
                        ? autoFillAnnualReport
                        : null;
                this.carriedForwardRates = comparative?.fxTranslation?.currentYear ?? null;
                this.carriedForwardMethods = comparative?.fxTranslation?.accountMethods ?? null;
                this.carriedForwardHistorical = comparative?.fxTranslation?.accountHistorical ?? null;
                this.carriedForwardBalanceDate = comparative?.generalInformation?.balanceSheetDate ?? null;
                this.carriedForwardRounding = comparative?.fxTranslation?.roundingAccounts?.currentYear ?? null;
                this.applyCarriedForwardRates();
            });

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());
    }

    private onSaveSuccessfully(): void {
        this.savedSuccessfully.next(true);
        this.fireSuccessNotification();
        // Rebaselines the dirty check — without it the unsaved-changes dialog fires after a save.
        this.form.markAsPristine();
        this.methodsDirty = false;

        if (this.advanceToTableOnSave) {
            this.advanceToTableOnSave = false;
            this.selectStep(this.stepConversionTable);
        }
    }

    private onSaveFailed(): void {
        this.advanceToTableOnSave = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    private applyAnnualReport(annualReport: IAnnualReport): void {
        const generalInformation = annualReport.generalInformation;
        this.functionalCurrencyName = generalInformation?.currency?.name ?? '';
        this.displayCurrencyName = generalInformation?.displayCurrency?.name ?? '';

        this.currencyPairLabel =
            this.functionalCurrencyName !== '' && this.displayCurrencyName !== ''
                ? `${this.functionalCurrencyName}/${this.displayCurrencyName}`
                : '';

        if (generalInformation?.balanceSheetDate != null) {
            this.balanceSheetDate = new Date(generalInformation.balanceSheetDate);
            const year = this.balanceSheetDate.getFullYear();
            const day = `${this.balanceSheetDate.getDate()}`.padStart(2, '0');
            const month = `${this.balanceSheetDate.getMonth() + 1}`.padStart(2, '0');

            this.currentYearLabel = `${year}`;
            this.previousYearLabel = `${year - 1}`;
            this.currentYearClosingDate = `${day}.${month}.${year}`;
            this.previousYearClosingDate = `${day}.${month}.${year - 1}`;
        }

        // The store re-emits on every report change; unsaved edits and a user unlock must survive.
        const fxTranslation = annualReport.fxTranslation;
        if (fxTranslation != null && this.form.pristine) {
            if (!this.hasUserOverriddenLock) {
                this.previousYearLocked = fxTranslation.previousYearLocked === true;
            }
            this.form.patchValue(
                {
                    currentYearClosing: fxTranslation.currentYear?.closing ?? null,
                    currentYearAverage: fxTranslation.currentYear?.average ?? null,
                    previousYearClosing: fxTranslation.previousYear?.closing ?? null,
                    previousYearAverage: fxTranslation.previousYear?.average ?? null,
                },
                { emitEvent: false },
            );
        }

        if (fxTranslation != null && !this.methodsDirty) {
            this.accountMethods = fxTranslation.accountMethods
                ? (JSON.parse(JSON.stringify(fxTranslation.accountMethods)) as Record<string, Record<string, IFxAccountMethods>>)
                : {};
            this.accountHistorical = fxTranslation.accountHistorical
                ? (JSON.parse(JSON.stringify(fxTranslation.accountHistorical)) as Record<
                      string,
                      Record<string, IFxAccountHistorical>
                  >)
                : {};
            this.roundingAccounts = fxTranslation.roundingAccounts
                ? (JSON.parse(JSON.stringify(fxTranslation.roundingAccounts)) as IFxRoundingAccounts)
                : {};
        }

        // Once the rates are saved, the remaining work lives in the table — open there. Only on
        // the first report emission of the visit; later ones (e.g. after a save) must not yank
        // the user off the step they chose.
        if (!this.hasAppliedInitialStep) {
            this.hasAppliedInitialStep = true;
            if (this.hasCompleteSavedRates(fxTranslation)) {
                this.selectStep(this.stepConversionTable);
            }
        }

        this.applyPreviousYearLockState();
        this.syncFooterButtons();
        this.applyCarriedForwardRates();
        this.updateConversionInput();
    }

    /** buildFxTranslation() constructs a fresh object — the new reference is what makes the
     *  OnPush conversion table redecorate via ngOnChanges. */
    private updateConversionInput(): void {
        this.conversionFxTranslation = this.buildFxTranslation();
    }

    onMethodChange(change: IFxMethodChange): void {
        const forPath = (this.accountMethods[change.fliPath] ??= {});
        const forAccount = (forPath[change.accountKey] ??= {});
        forAccount[change.year] = change.method;
        this.methodsDirty = true;
        this.syncFooterButtons();
        this.updateConversionInput();
    }

    onRoundingAccountChange(change: IFxRoundingAccountChange): void {
        if (change.account == null) {
            delete this.roundingAccounts[change.year];
        } else {
            this.roundingAccounts[change.year] = { ...change.account };
        }
        this.methodsDirty = true;
        this.syncFooterButtons();
        this.updateConversionInput();
    }

    onHistoricalEdit(request: IFxHistoricalEditRequest): void {
        this.historicalRequest = request;
    }

    onHistoricalCancel(): void {
        this.historicalRequest = null;
    }

    onHistoricalSave(payload: IFxHistoricalSavePayload): void {
        const forPath = (this.accountHistorical[payload.fliPath] ??= {});
        const forAccount = (forPath[payload.accountKey] ??= {});
        // Each scope writes only its own field — the prior-year editor never sees this year's
        // movements, so it must not overwrite them with an empty list.
        if (payload.year === 'previousYear') {
            forAccount.previousYearRate = payload.previousYearRate;
            // This rate and the account's prior-year balance ARE the anchor the whole history later
            // reconstructs from, and this is the only moment both are known. The anchor belongs to
            // the report where the account's chain STARTS — per ACCOUNT, not per report: a position
            // that first appears in the second year starts its chain there, and without an anchor it
            // would carry neither a history nor a prior-year rate into the third. Nothing carried for
            // this account means no earlier report anchored it.
            const carriedForAccount = this.rekeyedCarried(this.carriedForwardHistorical)?.[payload.fliPath]?.[
                payload.accountKey
            ];
            if (carriedForAccount == null && payload.previousYearRate != null) {
                const opening: IFxHistoricalOpening = {
                    balanceSheetDate:
                        this.currentAnnualReport?.generalInformation?.balancePreviousYearDate ?? undefined,
                    functionalAmount: payload.functionalOpening,
                    rate: payload.previousYearRate,
                };
                forAccount.archiveOpening = opening;
            }
        } else {
            forAccount.currentYearMovements = payload.currentYearMovements;
        }
        this.methodsDirty = true;
        this.historicalRequest = null;
        this.syncFooterButtons();
        this.updateConversionInput();
    }

    /**
     * Carries the prior close's rates into the prior-year fields, locked. Called
     * from both source subscriptions because either may emit first — it applies once, and only
     * once the report is known to have no rates of its own. Nothing is persisted here; the rates
     * reach the database through the normal save.
     */
    private applyCarriedForwardRates(): void {
        if (
            this.hasAppliedCarriedForwardRates ||
            this.hasUserOverriddenLock ||
            this.carriedForwardRates == null ||
            this.currentAnnualReport == null ||
            this.currentAnnualReport.fxTranslation != null ||
            this.form.dirty ||
            !this.hasFinStatements
        ) {
            return;
        }

        this.hasAppliedCarriedForwardRates = true;
        this.form.patchValue(
            {
                previousYearClosing: this.carriedForwardRates.closing,
                previousYearAverage: this.carriedForwardRates.average,
            },
            { emitEvent: false },
        );
        // The comparative reproduces the prior close: its current-year methods become this
        // report's prior-year methods, locked and unlocked together with the rates.
        const carriedMethods = this.rekeyedCarried(this.carriedForwardMethods);
        if (carriedMethods != null) {
            for (const [fliPath, accounts] of Object.entries(carriedMethods)) {
                for (const [accountKey, methods] of Object.entries(accounts)) {
                    if (methods.currentYear != null) {
                        const forPath = (this.accountMethods[fliPath] ??= {});
                        (forPath[accountKey] ??= {}).previousYear = methods.currentYear;
                    }
                }
            }
        }
        // Its choice for its own year squares the same prior year here — carried with it, locked.
        if (this.carriedForwardRounding != null) {
            this.roundingAccounts.previousYear = { ...this.carriedForwardRounding };
        }
        this.carryForwardHistoricalArchive();
        this.previousYearLocked = true;
        this.applyPreviousYearLockState();
        this.syncFooterButtons();
        this.updateConversionInput();
    }

    /**
     * Freezes the prior close into this report: its own archive plus the year it just closed,
     * the opening anchor, and the rate it ended at. Runs with the rest of the autofill, once.
     */
    private carryForwardHistoricalArchive(): void {
        const carriedHistorical = this.rekeyedCarried(this.carriedForwardHistorical);
        if (carriedHistorical == null) {
            return;
        }
        for (const [fliPath, accounts] of Object.entries(carriedHistorical)) {
            for (const [accountKey, carried] of Object.entries(accounts)) {
                const closedYear: IFxHistoricalArchiveYear[] =
                    (carried.currentYearMovements?.length ?? 0) > 0
                        ? [
                              {
                                  balanceSheetDate: this.carriedForwardBalanceDate ?? undefined,
                                  movements: carried.currentYearMovements as IFxMovement[],
                              },
                          ]
                        : [];
                const archive = [...(carried.archive ?? []), ...closedYear];
                const rate = this.closingRateOf(carried);
                if (archive.length === 0 && carried.archiveOpening == null && rate == null) {
                    continue;
                }
                const forPath = (this.accountHistorical[fliPath] ??= {});
                const forAccount = (forPath[accountKey] ??= {});
                if (archive.length > 0) {
                    forAccount.archive = archive;
                }
                // The anchor travels with the archive, untouched, for as long as the chain lives.
                if (carried.archiveOpening != null) {
                    forAccount.archiveOpening = carried.archiveOpening;
                }
                if (rate != null) {
                    forAccount.previousYearRate = rate;
                }
            }
        }
    }

    /**
     * The comparative's per-account settings re-keyed onto this report's accounts, so an account
     * renamed between the years keeps what it had (see FxTranslationHelper.rekeyByAccountNumber).
     */
    private rekeyedCarried<T>(
        carried: Record<string, Record<string, T>> | null,
    ): Record<string, Record<string, T>> | null {
        if (carried == null || !FxTranslationHelper.hasUsableStatements(this.finStatements)) {
            return carried;
        }

        return FxTranslationHelper.rekeyByAccountNumber(
            carried,
            FxTranslationHelper.accountKeysByFliPath(
                this.finStatements as IFinStatements[],
                this.currentAnnualReport?.groupingAndMasking,
            ),
        );
    }

    /**
     * Stores the anchor of every chain that starts here on a period rate (see
     * FxTranslationHelper.periodRateAnchors). Re-derived on every save, so it follows a changed
     * prior-year method or rate. A carried chain keeps the anchor it brought along.
     */
    private stampPeriodRateAnchors(): void {
        if (!FxTranslationHelper.hasUsableStatements(this.finStatements)) {
            return;
        }
        const carried = this.rekeyedCarried(this.carriedForwardHistorical);
        const anchors = FxTranslationHelper.periodRateAnchors(
            this.finStatements as IFinStatements[],
            this.currentAnnualReport?.groupingAndMasking,
            this.buildFxTranslation(),
            this.currentAnnualReport?.generalInformation?.balancePreviousYearDate ?? undefined,
        );
        for (const { fliPath, accountKey, opening } of anchors) {
            if (carried?.[fliPath]?.[accountKey] != null) {
                continue;
            }
            ((this.accountHistorical[fliPath] ??= {})[accountKey] ??= {}).archiveOpening = opening;
        }
    }

    /**
     * The rate the prior close actually ended at: its anchor, plus every movement it ever recorded,
     * as value over balance. Taken ONCE here and stored as this report's prior-year rate.
     *
     * Storing it matters — it is what makes the chain carry beyond a single generation. Re-deriving
     * it on every read instead would need the previous report's own stored rate, which from year
     * three on is itself only carried and therefore never persisted.
     */
    private closingRateOf(carried: IFxAccountHistorical): number | null {
        const opening = carried.archiveOpening;
        if (opening == null) {
            return null;
        }
        let functional = opening.functionalAmount;
        let display = this.round2(opening.functionalAmount * opening.rate);
        const years = [...(carried.archive ?? []), { movements: carried.currentYearMovements ?? [] }];
        for (const year of years) {
            for (const movement of year.movements ?? []) {
                functional = this.round2(functional + (movement.functionalAmount ?? 0));
                display = this.round2(display + this.round2((movement.functionalAmount ?? 0) * (movement.rate ?? 0)));
            }
        }

        return functional === 0 ? null : display / functional;
    }

    private round2(value: number): number {
        return FxTranslationHelper.round2(value);
    }

    /** `disableProceedButton` also gates the unsaved-changes dialog's "save and proceed" option. */
    private syncFooterButtons(): void {
        const isSaveable = this.form.valid;
        this.disableProceedButton = !isSaveable;
        this.footerExtraButtonActions[0].disabled = !isSaveable;
    }

    /** Disabled rather than readonly: a disabled control also suspends its required validator. */
    private applyPreviousYearLockState(): void {
        const controls = [this.form.get('previousYearClosing'), this.form.get('previousYearAverage')];
        controls.forEach((control) => {
            if (this.previousYearLocked) {
                control?.disable({ emitEvent: false });
            } else {
                control?.enable({ emitEvent: false });
            }
        });
    }

    get isPeriodStep(): boolean {
        return this.selectedTab === this.stepPeriodRates;
    }

    get nextButtonLabelKey(): string {
        return this.isPeriodStep ? 'aumico_annual_report_fx_table_step' : this.nextStepLabel;
    }

    get nextButtonRouterLink(): string | null {
        return this.isPeriodStep ? null : `../../${this.nextStepPath}`;
    }

    onTabClick(item: NavigationItem): void {
        const index = this.navigationItems.findIndex((nav) => nav.name === item.name);
        if (index !== -1) {
            this.selectStep(index);
        }
    }

    private selectStep(index: number): void {
        this.navigationItems.forEach((nav, i) => (nav.active = i === index));
        this.selectedTab = index;
    }

    /** All four period rates persisted on the report — the rate setup is complete. */
    private hasCompleteSavedRates(fxTranslation: IFxTranslation | undefined | null): boolean {
        return (
            fxTranslation?.currentYear?.closing != null &&
            fxTranslation.currentYear.average != null &&
            fxTranslation.previousYear?.closing != null &&
            fxTranslation.previousYear.average != null
        );
    }

    // #region Lock

    private onUnlockRequested(): void {
        this.showUnlockConfirmation = true;
    }

    /**
     * Unlocking asks first — it hands the carried prior year over to the user. Locking again needs
     * no confirmation: it only makes the fields read-only, and nothing entered is lost.
     */
    onLockToggleClicked(): void {
        if (this.previousYearLocked) {
            this.onUnlockRequested();

            return;
        }
        this.hasUserOverriddenLock = true;
        this.previousYearLocked = true;
        this.applyPreviousYearLockState();
        this.form.markAsDirty();
    }

    onUnlockConfirmed(): void {
        this.showUnlockConfirmation = false;
        this.hasUserOverriddenLock = true;
        this.previousYearLocked = false;
        this.applyPreviousYearLockState();
        this.form.markAsDirty();
    }

    onUnlockDeclined(): void {
        this.showUnlockConfirmation = false;
    }

    // #endregion

    // #region Rate file import

    onDragOver(event: DragEvent): void {
        event.preventDefault();
    }

    onFileDropped(event: DragEvent, target: 'currentYear' | 'previousYear'): void {
        event.preventDefault();
        const file = event.dataTransfer?.files?.item(0);
        if (file != null) {
            this.acceptFile(file, target);
        }
    }

    onFileSelected(event: Event, target: 'currentYear' | 'previousYear'): void {
        const file = (event.target as HTMLInputElement).files?.item(0);
        if (file != null) {
            this.acceptFile(file, target);
        }
    }

    private acceptFile(file: File, target: 'currentYear' | 'previousYear'): void {
        this.importErrors = { ...this.importErrors, [target]: undefined };
        const isExcel = /\.xlsx?$/i.test(file.name);
        const isWithinSizeLimit = file.size <= MAX_RATE_FILE_SIZE_MB * 1024 * 1024;
        if (!isExcel || !isWithinSizeLimit) {
            this.showImportError(target, { messageKey: 'aumico_annual_report_fx_rates_import_error' });
            return;
        }
        this.parseAndStage(file, target);
    }

    private parseAndStage(file: File, target: 'currentYear' | 'previousYear'): void {
        const reader = new FileReader();
        reader.onerror = () =>
            this.showImportError(target, { messageKey: 'aumico_annual_report_fx_rates_import_error' });
        reader.onload = () => {
            try {
                this.pendingSeries = this.parseRateSeries(reader.result as ArrayBuffer, target);
                this.importTargetYear = target;
                this.showImportConfirmation = true;
            } catch (error) {
                debug('rate file parse failed', error);
                this.showImportError(
                    target,
                    error instanceof RateFileError
                        ? error
                        : { messageKey: 'aumico_annual_report_fx_rates_import_error' },
                );
            }
        };
        reader.readAsArrayBuffer(file);
    }

    private showImportError(target: RateImportTarget, error: IRateImportError): void {
        this.importErrors = {
            ...this.importErrors,
            [target]: { messageKey: error.messageKey, params: error.params },
        };
    }

    /**
     * Only the «Date» and «Actual Rate» columns are read, wherever they sit — every other column of
     * the export is ignored. The currency columns are not checked on purpose: the confirmation dialog
     * shows the pair the rates are applied to, and that is what guards against a wrong file.
     */
    private parseRateSeries(buffer: ArrayBuffer, target: 'currentYear' | 'previousYear'): IParsedRateSeries {
        // No cellDates: SheetJS builds those Dates through the local zone's historic offset and can
        // land a day early. Date cells stay serial numbers and are read as calendar days below.
        const workbook = XLSX.read(buffer, { type: 'array' });
        const table = this.findRateTable(workbook);
        if (table == null) {
            throw new RateFileError('aumico_annual_report_fx_rates_import_error_columns');
        }

        const series: { date: Date; value: number }[] = [];
        table.rows.forEach((row) => {
            const date = this.toDate(row[table.dateColumn]);
            const value = this.toNumber(row[table.rateColumn]);
            if (date != null && value != null) {
                series.push({ date, value });
            }
        });

        if (series.length === 0) {
            throw new RateFileError('aumico_annual_report_fx_rates_import_error_rows');
        }
        series.sort((a, b) => a.date.getTime() - b.date.getTime());

        // A rate list may span several years; averaging the whole file would silently mix them.
        const targetYear = this.periodYear(target);
        const period = series.filter((entry) => entry.date.getFullYear() === targetYear);
        if (period.length === 0) {
            // Name the year looked for and the span the file does cover — the usual cause is a list
            // for another year, and the bare "nothing found" leaves the user guessing.
            throw new RateFileError('aumico_annual_report_fx_rates_import_error_period', {
                year: String(targetYear ?? ''),
                from: this.formatDay(series[0].date),
                to: this.formatDay(series[series.length - 1].date),
            });
        }

        const average = period.reduce((sum, entry) => sum + entry.value, 0) / period.length;
        const closingEntry = this.closingEntryOf(period, target);

        return {
            average,
            closing: closingEntry?.value ?? null,
            closingDate:
                closingEntry != null && !this.isSameDay(closingEntry.date, this.periodEndDate(target))
                    ? closingEntry.date
                    : null,
        };
    }

    /** The first sheet whose header row names both columns, and the rows below that header. */
    private findRateTable(
        workbook: XLSX.WorkBook,
    ): { rows: unknown[][]; dateColumn: number; rateColumn: number } | null {
        for (const sheetName of workbook.SheetNames) {
            const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], {
                header: 1,
                blankrows: false,
            });
            for (let index = 0; index < Math.min(rows.length, RATE_FILE_HEADER_SEARCH_ROWS); index++) {
                const headers = Array.from(rows[index] ?? [], (cell) =>
                    String(cell ?? '')
                        .trim()
                        .toLowerCase(),
                );
                const dateColumn = headers.indexOf(RATE_FILE_DATE_HEADER);
                const rateColumn = headers.indexOf(RATE_FILE_RATE_HEADER);
                if (dateColumn >= 0 && rateColumn >= 0) {
                    return { rows: rows.slice(index + 1), dateColumn, rateColumn };
                }
            }
        }

        return null;
    }

    /**
     * The rate on the balance sheet date. A list of business days has no row for a weekend, so when
     * only Saturday/Sunday lie between the last row and that date, the last row stands in for it. A
     * list that simply ends earlier yields none — its last rate is not the closing rate.
     */
    private closingEntryOf(
        period: { date: Date; value: number }[],
        target: 'currentYear' | 'previousYear',
    ): { date: Date; value: number } | null {
        const periodEnd = this.periodEndDate(target);
        if (periodEnd == null) {
            return null;
        }
        const candidates = period.filter((entry) => entry.date.getTime() <= periodEnd.getTime());
        const last = candidates[candidates.length - 1];
        if (last == null) {
            return null;
        }
        for (const day = new Date(last.date.getTime()); day.getTime() < periodEnd.getTime(); ) {
            day.setDate(day.getDate() + 1);
            if (day.getDay() !== 0 && day.getDay() !== 6) {
                return null;
            }
        }

        return last;
    }

    private periodYear(target: 'currentYear' | 'previousYear'): number | null {
        if (this.balanceSheetDate == null) {
            return null;
        }
        const year = this.balanceSheetDate.getFullYear();

        return target === 'currentYear' ? year : year - 1;
    }

    /** The report's own balance sheet date in the target year — the period is not always 31.12. */
    private periodEndDate(target: 'currentYear' | 'previousYear'): Date | null {
        const year = this.periodYear(target);
        if (this.balanceSheetDate == null || year == null) {
            return null;
        }

        return new Date(year, this.balanceSheetDate.getMonth(), this.balanceSheetDate.getDate());
    }

    private formatDay(date: Date): string {
        const pad = (value: number) => String(value).padStart(2, '0');

        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
    }

    private isSameDay(a: Date, b: Date | null): boolean {
        return (
            b != null &&
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate()
        );
    }

    /** Every date as local midnight of its calendar day: an Excel serial, `dd.mm.yyyy` or `yyyy-mm-dd`. */
    private toDate(value: unknown): Date | null {
        if (typeof value === 'number' && isFinite(value)) {
            const parts = XLSX.SSF.parse_date_code(value);
            return parts != null && parts.y > 1900 ? new Date(parts.y, parts.m - 1, parts.d) : null;
        }
        if (typeof value === 'string') {
            const swiss = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(value.trim());
            if (swiss != null) {
                return new Date(Number(swiss[3]), Number(swiss[2]) - 1, Number(swiss[1]));
            }
            const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
            if (iso != null) {
                return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
            }
        }
        return null;
    }

    private toNumber(value: unknown): number | null {
        if (typeof value === 'number' && isFinite(value)) {
            return value;
        }
        if (typeof value === 'string') {
            const parsed = Number(value.replace(',', '.').trim());
            return isFinite(parsed) && value.trim() !== '' ? parsed : null;
        }
        return null;
    }

    onImportConfirmed(): void {
        const parsed = this.pendingSeries;
        this.showImportConfirmation = false;
        this.pendingSeries = null;
        if (parsed == null) {
            return;
        }

        const patch: Record<string, number> = {};
        patch[`${this.importTargetYear}Average`] = parsed.average;
        if (parsed.closing != null) {
            patch[`${this.importTargetYear}Closing`] = parsed.closing;
        }
        this.form.patchValue(patch);
    }

    onImportDeclined(): void {
        this.showImportConfirmation = false;
        this.pendingSeries = null;
    }

    // #endregion

    // #region Save

    proceedButtonClicked = (): void => {
        this.advanceToTableOnSave = this.isPeriodStep;
        this.save();
    };

    override save(): Observable<void> {
        if (this.currentAnnualReport == null || this.form.invalid) {
            this.advanceToTableOnSave = false;
            return of(void 0);
        }

        this.stampPeriodRateAnchors();
        this.currentAnnualReport.fxTranslation = this.buildFxTranslation();
        this.dispatchSaveAnnualReport(this.currentAnnualReport);

        return of(void 0);
    }

    private buildFxTranslation(): IFxTranslation {
        const value = this.form.getRawValue();
        // Empty fields stay null — Number(null)/Number('') would coin a legal-looking rate of 0.
        const rate = (raw: unknown): number | null => (raw == null || raw === '' ? null : Number(raw));

        return {
            currentYear: {
                closing: rate(value.currentYearClosing),
                average: rate(value.currentYearAverage),
            },
            previousYear: {
                closing: rate(value.previousYearClosing),
                average: rate(value.previousYearAverage),
            },
            previousYearLocked: this.previousYearLocked,
            accountMethods: Object.keys(this.accountMethods).length > 0 ? this.accountMethods : undefined,
            accountHistorical:
                Object.keys(this.accountHistorical).length > 0 ? this.accountHistorical : undefined,
            roundingAccounts: Object.keys(this.roundingAccounts).length > 0 ? this.roundingAccounts : undefined,
        };
    }

    protected override isDataChanged(): Observable<boolean> {
        return of(this.currentAnnualReport).pipe(
            map((annualReport) => {
                if (this.methodsDirty) {
                    return true;
                }
                if (annualReport == null || this.form.pristine) {
                    return false;
                }
                const saved = annualReport.fxTranslation;
                const current = this.buildFxTranslation();

                return (
                    saved == null ||
                    saved.currentYear?.closing !== current.currentYear.closing ||
                    saved.currentYear?.average !== current.currentYear.average ||
                    saved.previousYear?.closing !== current.previousYear.closing ||
                    saved.previousYear?.average !== current.previousYear.average ||
                    (saved.previousYearLocked === true) !== (current.previousYearLocked === true)
                );
            }),
        );
    }

    // #endregion
}
